import { useQuery } from '@tanstack/react-query';
import { Button, Form, Select, Spin } from 'antd';
import staffAccountApiCall from 'apps/investcore/apiCalls/staff-accounts';
import userApiCall from 'apps/investment/apiCalls/user/list';
import { useEffect, useRef, useState } from 'react';
import { formItemLayout, tailFormItemLayout } from 'utils/formConfig';

const permissionOptions = [
  { value: 'read', label: 'Read' },
  { value: 'manage', label: 'Manage' },
  { value: 'admin', label: 'Admin' },
];

const ItemForm = ({
  form,
  submiting,
  onFinish,
  initialValues,
  name,
}: {
  form: any;
  submiting?: boolean;
  onFinish: (values: any) => void;
  initialValues?: any;
  name?: string;
}) => {
  // Editing only changes the permission; the staff/user pair is immutable.
  const isEdit = !!initialValues?.id;

  const { data: staffResponse, isLoading: isLoadingStaff } = useQuery({
    queryKey: [staffAccountApiCall.list.queryKey, { page_size: 100 }],
    queryFn: () => staffAccountApiCall.list.queryFn({ page_size: 100 }),
  });

  const [userSearch, setUserSearch] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [fetchOnFocus, setFetchOnFocus] = useState(false);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => setDebouncedUserSearch(userSearch),
      300
    );
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [userSearch]);

  const { data: userResponse, isLoading: isLoadingUsers } = useQuery({
    queryKey: [userApiCall.queryKey, { search: debouncedUserSearch }],
    queryFn: () => userApiCall.queryFn({ search: debouncedUserSearch }),
    enabled: debouncedUserSearch.length > 0 || fetchOnFocus,
  });

  const staffOptions = (staffResponse?.data || []).map(item => ({
    value: item.id,
    label: `${item.email}${item.name ? ` - ${item.name}` : ''}`,
  }));
  const userOptions = (userResponse?.data || []).map(item => ({
    value: item.id,
    label: `${item.email || item.phone || item.id}${
      item.name ? ` - ${item.name}` : ''
    }`,
  }));

  // On edit the user list isn't loaded, so provide a label for the current
  // user from the detail response instead of showing a raw UUID.
  if (
    isEdit &&
    initialValues?.user_id &&
    !userOptions.some(o => o.value === initialValues.user_id)
  ) {
    userOptions.push({
      value: initialValues.user_id,
      label: `${
        initialValues.user_email ||
        initialValues.user_phone ||
        initialValues.user_id
      }${initialValues.user_name ? ` - ${initialValues.user_name}` : ''}`,
    });
  }

  return (
    <Form
      {...formItemLayout}
      form={form}
      name={name || 'staff_user_assignment_form'}
      onFinish={values => onFinish(values)}
      initialValues={{ permission: 'read', ...initialValues }}
      scrollToFirstError
    >
      <Form.Item
        name="staff_account_id"
        label="Staff"
        rules={[{ required: true, message: 'Please select a staff account' }]}
      >
        <Select
          showSearch
          disabled={isEdit}
          loading={isLoadingStaff}
          options={staffOptions}
          optionFilterProp="label"
          placeholder="Select staff"
        />
      </Form.Item>

      <Form.Item
        name="user_id"
        label="User"
        rules={[{ required: true, message: 'Please select a user' }]}
      >
        <Select
          showSearch
          disabled={isEdit}
          loading={isLoadingUsers}
          options={userOptions}
          optionFilterProp="label"
          filterOption={false}
          placeholder="Select user"
          onSearch={val => setUserSearch(val)}
          onFocus={() => setFetchOnFocus(true)}
          notFoundContent={isLoadingUsers ? <Spin size="small" /> : null}
        />
      </Form.Item>

      <Form.Item
        name="permission"
        label="Permission"
        rules={[{ required: true, message: 'Please select a permission' }]}
        extra="Access level for this user. Not enforced yet."
      >
        <Select options={permissionOptions} />
      </Form.Item>

      <Form.Item {...tailFormItemLayout}>
        <Button type="primary" htmlType="submit" loading={submiting}>
          Save
        </Button>
      </Form.Item>
    </Form>
  );
};

export default ItemForm;
